/**
 * B2B is a closed wholesale site: nothing is visible until you log in.
 *
 * Every route is gated except the handful below. This replaces the inherited B2C
 * behaviour, where `middleware/auth.ts` sent guests to `/?auth=login` and a
 * modal handled login on an otherwise public storefront.
 */
const PUBLIC_ROUTE_NAMES = new Set([
  LOGIN_ROUTE_NAME,
  'remind-password',
  'change-password',
  'logout',
])

/** `@nuxtjs/i18n` suffixes every route name with `___<locale>`. */
function baseRouteName(name: unknown): string {
  return String(name ?? '').replace(/___[a-z-]+$/i, '')
}

/**
 * The offer email's link — `/paskyra/mano-pasiulymai/{number}/?h={hash}` — opens
 * without a session: the per-recipient hash is the credential for reading the
 * offer (`OfferBuyerView`), as it was on the Smarty site. The same URL without
 * `h` is the manager's page and stays gated.
 */
function isOfferHashLink(to: { name?: unknown; query: Record<string, unknown> }): boolean {
  return baseRouteName(to.name) === 'account-offers-slug'
    && typeof to.query.h === 'string'
    && /^[a-f0-9]{32,128}$/i.test(to.query.h)
}

export default defineNuxtRouteMiddleware(async (to) => {
  const store = useCustomerStore()
  const isPublicRoute = PUBLIC_ROUTE_NAMES.has(baseRouteName(to.name)) || isOfferHashLink(to)

  const toLogin = (expired: boolean) => {
    const localeRoute = useLocaleRoute()
    const target = localeRoute({ name: LOGIN_ROUTE_NAME })
    if (!target) return

    // Only round-trip back to a real destination; landing on the site root is the
    // default post-login target anyway, so it would just add query noise.
    const redirect = baseRouteName(to.name) === 'index' ? undefined : to.fullPath

    return navigateTo({
      path: target.path,
      query: {
        ...(redirect ? { redirect } : {}),
        ...(expired ? { expired: '1' } : {}),
      },
    })
  }

  // Renew a stale session BEFORE the page renders — deliberately ahead of the
  // public-route check below, so a dead cookie is cleaned up even on the login
  // page. Without that, `middleware/guest.ts` saw `isLoggedIn` (the cookie is
  // still there, the token inside it is not usable) and bounced the visitor back
  // to a page whose every request 401'd: the app deadlocked with no way in.
  //
  // Doing it here rather than reacting to the first 401 also matters for the
  // cookie itself. A 401 surfacing mid-render throws a fatal error, and Nuxt only
  // flushes server-side `useCookie` writes on `app:rendered` / `app:redirected` —
  // so `logout()`'s clear never reached the browser and the next request repeated
  // the whole sequence. A redirect from middleware does flush it.
  if (store.isLoggedIn && accessTokenExpired(store.accessToken)) {
    try {
      // De-duplicated inside the store: concurrent callers share one refresh, and
      // a failure has already called `logout()` by the time we get here.
      await store.refresh()
    } catch {
      // `logout()` has already cleared the session, so a visitor who is *on* a
      // public route simply gets to stay there — redirecting login → login would
      // only bounce the page and leave a `?redirect=/lt/login/` self-reference in
      // the url.
      if (isPublicRoute) return
      return toLogin(true)
    }
  }

  if (store.isLoggedIn || isPublicRoute) return

  return toLogin(false)
})
